import { useEffect, useState, useMemo } from 'react';
import '../page-list/PageList.css';
import './ImportQueuePage.css';
import styles from '../clients/ClientList.module.scss';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import Spinner from '../ui/spinners/Spinner';
import '../page-list/PageList.css';
import TextRangeDatePicker from '../helpers/TextRangeDatePicker';
import SearchComponent from '../helpers/search/SearchComponent';
import { subDays, format } from 'date-fns';
import { SortDirections } from '../../hooks/useTableSort';
import PaginationComponent from '../helpers/PaginationComponent';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import {
    fetchMultiTenantImports,
    getImportReport,
    getOriginalFile,
    deleteImportReview,
    addImportReview,
} from '../../services/importService';
import { getBadgeStatus } from '../helpers/getBadgeStatus';
import { logEvent } from '../../services/loggingService';
import { LogEventType } from '../../utils/data/enums';
import { handleNavigateToClient } from '../../utils/utils';
import { useAuthContext } from '../../store/AuthContext';
import Toast from '../ui/toasts/Toast';
import { SecurityAttributes } from '../../utils/data/enums';
import ImportReviewPopup from './ImportReviewPopup';
import { ROUTES } from '../../constants/routes';
import { DownloadIcon } from '../ui/svg/DownloadIcon';
import { getText } from '../../i18n';

const getImportTypeDisplayLabel = item => {
    const importTemplateType = (item?.importTemplateType || '').trim();

    if (importTemplateType.length > 0) {
        return importTemplateType;
    }

    const explicitType = (
        item?.type ||
        item?.importType ||
        item?.category ||
        ''
    ).trim();

    if (explicitType.length > 0) {
        return explicitType;
    }

    if (item?.importTypeId === 0) {
        return getText('msg_09cb6197bf14');
    }

    return '';
};

const MultiTenantImportQueue = () => {
    const defaultStartDate = subDays(new Date(), 7);
    const defaultEndDate = new Date();
    const pageSize = 50;

    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [imports, setImports] = useState([]);
    const [onlyFavorites, setOnlyFavorites] = useState(false);
    const [selections, setSelections] = useState({});
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const [sortConfig, setSortConfig] = useState({
        key: 'importDate',
        direction: SortDirections.DESCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [modalSettings, setModalSettings] = useState({
        x: 0,
        y: 0,
        reviewedBy: '',
        reviewedAt: '',
    });
    const [modalVisible, setModalVisible] = useState(false);

    const { user, hasAttribute } = useAuthContext();

    useEffect(() => {
        loadImports();
    }, []);

    useEffect(() => {
        loadImports();
    }, [startDate, endDate, sortConfig, onlyFavorites, currentPage]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            loadImports();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
    };

    const handleSearch = searchBy => {
        setSearchTerm(searchBy);
    };

    const loadImports = async () => {
        try {
            setIsLoading(true);
            const fetchedImports = await fetchMultiTenantImports(
                format(startDate, 'yyyy-MM-dd'),
                format(endDate, 'yyyy-MM-dd'),
                searchTerm,
                onlyFavorites,
                sortConfig.key,
                sortConfig.direction,
                currentPage,
                pageSize,
            );

            const formattedImports = formatImportData(
                fetchedImports.viewModels,
            );

            setImports(formattedImports);
            setBaseFilteredData(formattedImports);
            setTotalPages(Math.ceil(fetchedImports.pageTotal));
        } catch (error) {
            if (error.name === 'AbortError') {
                return null;
            }
            console.error('Failed to fetch imports', error);
            Toast.error(getText('msg_23c89b693b85'));
        } finally {
            setIsLoading(false);
        }
    };

    const formatImportData = viewModels => {
        return viewModels.map(item => {
            const resultsReport = item.reports.find(
                ({ importReportTypeId }) => importReportTypeId === 2,
            );

            let reviewedByName =
                item.reviewedAt !== null
                    ? item.reviewedByFirstName + ' ' + item.reviewedByLastName
                    : '';
            let reviewedAt =
                item.reviewedAt !== null
                    ? getDateFromZulu(item.reviewedAt)
                    : null;

            let canDeleteReview = true;

            // if the user doesnt have enhanced review access they cant delete someone elses review
            if (
                item.reviewedAt !== null &&
                user.id !== item.reviewedBy &&
                !hasAttribute(
                    SecurityAttributes.MultiImportQueueEnhancedReviewAccess,
                )
            )
                canDeleteReview = false;

            const importTypeLabel = getImportTypeDisplayLabel(item);

            return {
                id: item.id,
                importDate: getDateFromZulu(item.createdUTC),
                tenantId: item.tenantId,
                template: item.template || `Standard ${importTypeLabel}`,
                importType: importTypeLabel,
                status: item.status,
                test: item.isTest,
                submittedBy: item.createdBy,
                records:
                    item.totalRecords > 0
                        ? `${item.totalRecords - item.errors}/${item.totalRecords}`
                        : '',
                resultsReportId: resultsReport ? resultsReport.id : 0,
                displayFileName: item.displayFileName,
                fileType: item.fileType,
                totalRecords: item.totalRecords,
                errors: item.errors,
                canDelete: item.canDelete,
                canBeReviewed: item.canBeReviewed,
                isFavorite: item.isFavorite,
                reviewedByName: reviewedByName,
                reviewedAt: reviewedAt,
                clientName: item.clientName,
                clientCode: item.clientCode,
                tier: item.tier,
                canDeleteReview: canDeleteReview,
            };
        });
    };

    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filteredData = baseFilteredData.filter(sso =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(sso[key]),
            ),
        );

        return Array.from(new Set(filteredData.map(item => item[column])))
            .filter(Boolean) // Remove null/undefined values
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    const handleSort = key => {
        setSortConfig(prevConfig => ({
            key,
            direction:
                prevConfig.key === key &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const handleSortClient = () => handleSort('clientName');
    const handleSortTier = () => handleSort('tier');
    const handleSortImportDate = () => handleSort('importDate');
    const handleSortImportId = () => handleSort('id');
    const handleSortName = () => handleSort('template');
    const handleSortImportType = () => handleSort('importType');
    const handleSortStatus = () => handleSort('status');
    const handleSortReview = () => handleSort('review');

    const downloadOriginalFile = (id, tenantId, fileName) => {
        const anchor = document.createElement('a');
        document.body.appendChild(anchor);

        getOriginalFile(id, tenantId).then(blobby => {
            const objectUrl = window.URL.createObjectURL(blobby);
            anchor.href = objectUrl;
            anchor.download = encodeURIComponent(fileName);
            anchor.click();
            window.URL.revokeObjectURL(objectUrl);
        });

        document.body.removeChild(anchor);
    };

    const downloadReport = (id, tenantId, type) => {
        const anchor = document.createElement('a');
        document.body.appendChild(anchor);

        getImportReport(id, tenantId).then(blobby => {
            const objectUrl = window.URL.createObjectURL(blobby);
            anchor.href = objectUrl;
            anchor.download = createReportFileName(id, type);
            anchor.click();
            window.URL.revokeObjectURL(objectUrl);
            logEvent(
                LogEventType.DataDownloaded,
                `Imports queue report downloaded`,
            );
        });

        document.body.removeChild(anchor);
    };

    const createReportFileName = (id, type) => {
        return `import ${encodeURIComponent(id)} ${encodeURIComponent(type)} report.csv`;
    };

    const handlerReviewClick = async importId => {
        let importForReview = imports.find(i => i.id === importId);

        // if import has not been reviewed add it
        if (importForReview.reviewedAt === null) {
            try {
                var review = await addImportReview(importId);

                const firstName = user?.first_name;
                const lastName = user?.last_name;

                importForReview.reviewedByName = firstName + ' ' + lastName;
                importForReview.reviewedAt = getDateFromZulu(
                    review.data.createdUTC,
                );

                setImports([...imports]);

                Toast.success(getText('msg_a8c0afecb936'));
            } catch (error) {
                console.error('Failed to add import review', error);
                Toast.error(getText('msg_e5b79213bf87'));
            }
        }
        // if import has been reviewed delete it
        else {
            try {
                await deleteImportReview(importId);

                importForReview.reviewedAt = null;
                importForReview.reviewedByName = '';

                setImports([...imports]);

                Toast.success(getText('msg_cc7d100e7c6b'));
            } catch (error) {
                console.error('Failed to delete import review', error);
                Toast.error(getText('msg_df2e36226995'));
            }
        }
    };

    const handleReviewPopupClick = (event, reviewedBy, reviewedAt) => {
        const rect = event.target.getBoundingClientRect();
        setModalSettings({
            x: rect.left - 100,
            y: rect.top - 100,
            reviewedBy,
            reviewedAt,
        });
        setModalVisible(true);
    };

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="float-start d-flex align-items-center">
                            <div>
                                <p className="page-title card-title ms-4 me-5">
                                    {getText('msg_e42328ac01cd')}
                                </p>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{ marginTop: '10px' }}
                                >
                                    {getText('msg_30a716dfc695')}
                                </p>
                            </div>
                        </div>

                        <div className="float-end d-flex align-items-center">
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                        </div>
                    </div>

                    <div className="page-title card-title ms-4 me-5">
                        <TextRangeDatePicker
                            startDate={startDate}
                            endDate={endDate}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                        />
                    </div>

                    <div className="page-title card-title ms-4 me-5 mb-5">
                        <div className={styles.searchWrapper}>
                            <SearchComponent
                                onSearch={event => handleSearch(event)}
                                placeholder={getText('msg_bce06414177f')}
                                initialValue={searchTerm}
                                autoFocus={true}
                            />
                        </div>
                    </div>

                    <div
                        className="table-responsive-xxl w-100"
                        style={{
                            width: '100%',
                            overflowX: 'auto',
                            position: 'relative',
                            height: 'fit-content',
                        }}
                    >
                        <table className="table table-hover align-middle pinned-table">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th className="text-center pin pin-1">
                                        {onlyFavorites ? (
                                            <i
                                                className="bi bi-star-fill"
                                                style={{
                                                    color: '#FFA800',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() =>
                                                    setOnlyFavorites(false)
                                                }
                                            ></i>
                                        ) : (
                                            <i
                                                className="bi bi-star"
                                                style={{
                                                    color: 'lightgray',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() =>
                                                    setOnlyFavorites(true)
                                                }
                                            ></i>
                                        )}
                                    </th>

                                    <th className="pin pin-2">
                                        <span className="table-column-header-text">
                                            {getText('msg_1bdd79b12628')}
                                        </span>
                                        <span
                                            onClick={handleSortClient}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'clientName' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>

                                    <th className="pin pin-3">
                                        <span className="table-column-header-text">
                                            {getText('msg_5bd44ebe63cd')}
                                        </span>
                                        <span
                                            onClick={handleSortTier}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'tier' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th className="pin pin-4">
                                        <span className="table-column-header-text">
                                            {getText('msg_60a57de73d21')}
                                        </span>
                                        <span
                                            onClick={handleSortImportDate}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'importDate' &&
                                                        sortConfig.direction ===
                                                            SortDirections.ASCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_89f89c02cf47')}
                                        </span>
                                        <span
                                            onClick={handleSortImportId}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'importId' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('id');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['id']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'id' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_709a23220f2c')}
                                        </span>
                                        <span
                                            onClick={handleSortName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'name' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('template');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['template']?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal === 'template' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_78cfb1f529c1')}
                                        </span>
                                        <span
                                            onClick={handleSortImportType}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'importType' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('importType');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['importType']
                                                    ?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'importType' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_bae7d5be7082')}
                                        </span>
                                        <span
                                            onClick={handleSortStatus}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'status' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('status');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['status']?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal === 'status' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_e51c55255be9')}
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_8bf95ea37256')}
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_c0a8060f3b11')}
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_e29a79fe0c34')}
                                        </span>
                                        <span
                                            onClick={handleSortReview}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'review' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {imports.length <= 0 ? (
                                    <td colSpan={12}>
                                        <NotFoundBox
                                            backgroundColor="#f0f4ff"
                                            title={getText('msg_aca14ec90fb5')}
                                            description={getText(
                                                'msg_5328029ad816',
                                            )}
                                        />
                                    </td>
                                ) : (
                                    ''
                                )}

                                {filteredData.map((importRecord, index) => (
                                    <tr key={index}>
                                        <td className="item-page-name pin pin-1">
                                            {importRecord.isFavorite ? (
                                                <i
                                                    className="bi bi-star-fill"
                                                    style={{
                                                        color: '#FFA800',
                                                        cursor: 'pointer',
                                                    }}
                                                ></i>
                                            ) : (
                                                <i
                                                    className="bi bi-star"
                                                    style={{
                                                        color: 'lightgray',
                                                        cursor: 'pointer',
                                                    }}
                                                ></i>
                                            )}
                                        </td>

                                        <td
                                            className="titleCell pin pin-2"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                handleNavigateToClient(
                                                    importRecord.tenantId,
                                                    importRecord.clientCode,
                                                    importRecord.clientName,
                                                    false,
                                                    true,
                                                    ROUTES.IMPORT_QUEUE,
                                                )
                                            }
                                            onKeyDown={e => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.key === ' '
                                                ) {
                                                    handleNavigateToClient(
                                                        importRecord.tenantId,
                                                        importRecord.clientCode,
                                                        importRecord.clientName,
                                                        false,
                                                        true,
                                                        ROUTES.IMPORT_QUEUE,
                                                    );
                                                }
                                            }}
                                        >
                                            {importRecord.clientName}
                                        </td>

                                        <td className="subtitleCell pin pin-3">
                                            {importRecord.tier}
                                        </td>
                                        <td className="subtitleCell pin pin-4">
                                            {importRecord.importDate}
                                        </td>
                                        <td className="subtitleCell">
                                            {importRecord.id}
                                        </td>
                                        <td className="subtitleCell">
                                            {importRecord.template}
                                        </td>
                                        <td className="subtitleCell">
                                            {importRecord.importType}
                                        </td>
                                        <td>
                                            <span
                                                className={`item-page-status badge-base ${getBadgeStatus().Class(
                                                    importRecord.status,
                                                )}`}
                                            >
                                                <svg
                                                    className="me-1"
                                                    width={8}
                                                    height={8}
                                                    viewBox="0 0 8 8"
                                                    fill="none"
                                                >
                                                    <circle
                                                        cx={4}
                                                        cy={4}
                                                        r={3}
                                                        fill={getBadgeStatus().SVGColor(
                                                            importRecord.status,
                                                        )}
                                                    />
                                                </svg>
                                                {importRecord.status}
                                            </span>
                                        </td>
                                        <td className="subtitleCell">
                                            {importRecord.records
                                                ? importRecord.records
                                                : '-'}
                                        </td>
                                        <td
                                            onClick={() => {
                                                importRecord.resultsReportId ??
                                                    0 != 0;

                                                downloadReport(
                                                    importRecord.resultsReportId,
                                                    importRecord.tenantId,
                                                    'results',
                                                );
                                            }}
                                        >
                                            {importRecord.resultsReportId >
                                                0 && <DownloadIcon />}
                                        </td>

                                        <td
                                            onClick={() =>
                                                downloadOriginalFile(
                                                    importRecord.id,
                                                    importRecord.tenantId,
                                                    importRecord.displayFileName,
                                                )
                                            }
                                        >
                                            <DownloadIcon />
                                        </td>

                                        <td>
                                            {importRecord.canBeReviewed && (
                                                <div className="d-flex align-items-center">
                                                    <input
                                                        id={importRecord.id}
                                                        checked={
                                                            importRecord.reviewedAt !==
                                                            null
                                                        }
                                                        onChange={() =>
                                                            handlerReviewClick(
                                                                importRecord.id,
                                                            )
                                                        }
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        disabled={
                                                            !importRecord.canDeleteReview
                                                        }
                                                    />
                                                    <span
                                                        style={{
                                                            marginLeft: '5px',
                                                            marginTop: '3px',
                                                        }}
                                                        className="titleCell"
                                                        onClick={event =>
                                                            handleReviewPopupClick(
                                                                event,
                                                                importRecord.reviewedByName,
                                                                importRecord.reviewedAt,
                                                            )
                                                        }
                                                    >
                                                        {importRecord.canDeleteReview ? (
                                                            <div>
                                                                {' ' +
                                                                    importRecord.reviewedByName}
                                                            </div>
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    color: 'var(--iNGAGED-Neutral-Color3)',
                                                                }}
                                                            >
                                                                {' ' +
                                                                    importRecord.reviewedByName}
                                                            </div>
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {totalPages > 0 && (
                        <div className="mt-auto">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </div>
            </div>
            {modalVisible && (
                <ImportReviewPopup
                    x={modalSettings.x}
                    y={modalSettings.y}
                    onClose={() => setModalVisible(false)}
                    reviewedBy={modalSettings.reviewedBy}
                    reviewedAt={modalSettings.reviewedAt}
                />
            )}
        </div>
    );
};

export default MultiTenantImportQueue;
